/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import React, { Fragment } from 'react';
import '!file-loader?name=[name].[ext]!./favicon.ico';
import { polyfillIntl } from 'Base/Provider/Polyfills/intl';
import 'core-js';

import 'file-loader?name=[name].[ext]!./.htaccess';
import 'moment/locale/pt-br';
import ReactDOM from 'react-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { applyRouterMiddleware, browserHistory, Router } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';
import useScroll from 'react-router-scroll/lib/useScroll';

import 'regenerator-runtime/runtime';
import createStandaloneRoutes from 'routes/standalone';
import '@quintoandar/cozy-typeface';
import 'sanitize.css/sanitize.css';

import store from 'store';

import '@quintoandar/cozy-theme/themes/default.css';
import '@quintoandar/cozy-theme/themes/inmuebles24.css';
import '@quintoandar/cozy-core/styles/index.css';

import { Theme, ThemeWrapper } from '@quintoandar/cozy-core/theming';

import { detectLocale } from '@quintoandar/js-intl';

import { RUMProvider } from '@quintoandar/rum-react';

import { init } from '@quintoandar/fwp-error-monitoring';

import { FrontendObservability } from '@quintoandar/fwp-o11y-react';

import '@quintoandar/typeface';

import { initializeAuth } from 'utils/auth';
import ReduxLegacyContextBridge from 'shims/reduxLegacyContext';

// Load the favicon and the .htaccess file
/* eslint-disable import/no-unresolved, import/extensions */
import App from 'containers/App';
/* eslint-enable import/no-unresolved, import/extensions */

import { makeSelectLocationState } from 'containers/App/selectors';

import LanguageProvider from 'containers/LanguageProvider';
import { getMessages, getCozyThemeName } from 'i18n';

import GlobalStyle from './global-styles';

init({
  enabled: process.env.NODE_ENV === 'production',
  dsn: process.env.SENTRY_DSN,
  environment: process.env.SENTRY_ENVIRONMENT,
  release: process.env.SENTRY_RELEASE,
  ignoreErrors: [/network error/i],
});

const MOUNT_NODE = document.getElementById('app');

// Sync history and store, as the react-router-redux reducer
// is under the non-default key ("routing"), selectLocationState
// must be provided for resolving how to retrieve the "route" in the state
const history = syncHistoryWithStore(browserHistory, store, {
  selectLocationState: makeSelectLocationState(),
});

// Setup routes
const routes = createStandaloneRoutes(store, App);

const noop = () => {};

const queryClient = new QueryClient();

const render = (messages, userLocale) => {
  const currentTheme = getCozyThemeName(userLocale);
  const shouldEnableFrontendObservability = Boolean(
    process.env.GRAFANA_FARO_URL &&
      process.env.GRAFANA_FARO_KEY &&
      process.env.APP_NAME,
  );

  ReactDOM.render(
    <>
      {shouldEnableFrontendObservability ? (
        <FrontendObservability
          faroUrl={process.env.GRAFANA_FARO_URL}
          faroApiKey={process.env.GRAFANA_FARO_KEY}
          appName={process.env.APP_NAME}
          environment={process.env.APP_ENV}
        />
      ) : null}
      <QueryClientProvider client={queryClient}>
        <ThemeWrapper name={Theme[currentTheme]}>
          <Provider store={store}>
            <ReduxLegacyContextBridge store={store}>
              <LanguageProvider locale={userLocale} messages={messages}>
                <RUMProvider callback={noop}>
                  <Fragment>
                    <Router
                      history={history}
                      render={
                        // Scroll to top when going to a new page to imitate browser default behavior
                        applyRouterMiddleware(useScroll())
                      }
                      routes={routes}
                    />
                    <GlobalStyle />
                  </Fragment>
                </RUMProvider>
              </LanguageProvider>
            </ReduxLegacyContextBridge>
          </Provider>
        </ThemeWrapper>
      </QueryClientProvider>
    </>,
    MOUNT_NODE,
  );

  initializeAuth();
};

const locale = detectLocale();

// Chunked polyfill for browsers without Intl support
polyfillIntl(locale)
  .then(async () => {
    const messages = await getMessages(locale);
    render(messages, locale);
  })
  .catch((err) => {
    throw err;
  });

// Created a new NODE_ENV just for this case
// Running whyDidYouUpdate while on development mode REALLY slows down the app
if (process.env.NODE_ENV === 'render-audit') {
  const { whyDidYouUpdate } = require('why-did-you-update'); // eslint-disable-line
  whyDidYouUpdate(React);
}
